import { gql, useQuery } from '@apollo/client';
import { splitIncludedExcluded } from '@openx/components-targeting/Targeting/shared/InventoryAndContent/utils';
import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext';
import type {
  InstancesByIdObject,
  InventoryTargetingContent,
  TargetableInventoryEntities,
} from '@openx/types/targeting/inventoryAndContent';
import type { TargetingProviderConfig } from '@openx/types/targeting/targetingProviderConfig';
import { buildSearchQueryFromInventoriesById } from '@openx/utils/state/packages/inventories/utils';
import mergeWith from 'lodash/mergeWith';
import { useMemo } from 'react';
import type { InvSearchQuery, InvSearchQueryVariables } from '../../../../graphql/query/options/InvSearch.generated';
import { isInvList } from './isInvList';

export const useInventoriesFetch: TargetingProviderConfig['useInventoriesFetch'] = (
  inventoryPayload: InventoryTargetingContent<TargetableInventoryEntities>,
  idByInstances?: InstancesByIdObject
) => {
  const { useCurrentInstanceState } = useTargetingContext();
  const { currentInstanceUid } = useCurrentInstanceState();

  const queries = useMemo(() => {
    const currentInstance = currentInstanceUid ? { [currentInstanceUid]: { ...inventoryPayload } } : {};
    const instances = { ...idByInstances, ...currentInstance };

    const queries = Object.entries(instances).reduce(
      (prev, [key, val]) => {
        const searchConfig = mergeWith(
          val.includes ?? {},
          val.excludes ?? {},
          (includesInstancesId, excludesInstancesId) => `${includesInstancesId},${excludesInstancesId}`
        );
        const phrase = buildSearchQueryFromInventoriesById(searchConfig);
        if (phrase !== '()') {
          prev[key] = phrase;
        }
        return prev;
      },
      {} as { [key: string]: string }
    );

    return queries;
  }, [idByInstances, currentInstanceUid, inventoryPayload]);

  const skip = !queries || Object.keys(queries).length === 0;
  const query = useMemo(() => {
    const innerQ = Object.entries(queries)
      .map(
        ([key, phrase], idx) => `query${idx}: invOperatorSearch(instance_uid: "${key}", phrase: "${phrase.replaceAll(
          '"',
          '\\"'
        )}") {
        uid
        id
        name
        type
        account_uid
        parent_name
        instance_uid
        type_full
      }
      `
      )
      .join('');
    const qs = innerQ && innerQ.length > 0 ? `query InvSearch {${innerQ}}` : 'query InvSearch { ping }';
    return gql(qs);
  }, [queries]);

  const { data, loading } = useQuery<InvSearchQuery, InvSearchQueryVariables>(query, { skip });

  if (data) {
    const results = Object.values(data ?? {}).map(r => r ?? []);
    if (typeof results !== 'string') {
      if (isInvList(results)) {
        return { inventories: splitIncludedExcluded(results, inventoryPayload.excludes), loading };
      }
    }
  }

  return { inventories: null, loading: false };
};
